import { type ComponentType } from 'react';
import { type LoaderFunction, type RouteObject, redirect } from 'react-router-dom';
import { routeAuthMagic } from './app/components/auth-magic/auth-magic.route';
import { routeJoinFunnel } from './app/components/join-funnel/join-funnel.routes';
import { routePayment } from './app/components/payment/payment.route';
import { routeSignIn } from './app/components/sign-in/sign-in.route';
import { routeSignUp } from './app/components/sign-up/sign-up.route';
import { ChunkErrorBoundary } from './components/chunk-error-boundary/chunk-error-boundary';
import Layout from './components/layout-auth/layout-auth';
import MainLayout from './components/main-layout/main-layout';
import {
  ROUTE_ACCOUNT,
  ROUTE_ACCOUNT_EDIT_COMMUNICATION_PREFERENCES,
  ROUTE_ACCOUNT_EDIT_MEMBERSHIP,
  ROUTE_ACTION_PLAN,
  ROUTE_BIOMARKER_DETAIL,
  ROUTE_BUILD_ACTION_PLAN,
  ROUTE_CONSENT,
  ROUTE_DASHBOARD,
  ROUTE_HEALTH_DATA,
  ROUTE_LABS,
  ROUTE_LABS_ALL,
  ROUTE_LABS_ORDER,
  ROUTE_LABS_ORDER_STEP_1,
  ROUTE_LABS_ORDER_STEP_2,
  ROUTE_LABS_ORDER_STEP_3,
  ROUTE_LABS_ORDER_STEP_5,
  ROUTE_LOADING_SCREEN,
  ROUTE_MULTI_DISEASE_WELCOME,
  ROUTE_QUESTIONNAIRE,
  ROUTE_QUESTIONNAIRE_V2,
  ROUTE_RISK_ANALYSIS,
  ROUTE_BLOODWORK_REVEAL,
  ROUTE_RISK_SCORE_REVEAL,
  ROUTE_RISK_TREND,
  ROUTE_SIGN_IN,
  ROUTE_SIGN_UP,
  ROUTE_SINGLE_CANCER_VIEW,
  ROUTE_TEST_DETAILS,
  ROUTE_TESTING_PLAN,
  ROUTE_TESTING_PLAN_COMPLETE,
} from './constants/routes';
import authService from './services/auth.service';
import { hasWorkosSession } from './services/workos-auth-bridge';
import { routeQuizletVariant } from './app/components/quizletv2/quizlet-variant.route';
import { generateAllQuizletRoutes } from './app/components/quizletv2/quizlet-generic.route';
import { getEnabledQuizletVersions } from './app/components/quizletv2/quizlet-config';
import setTitleFromLoader, { setTitle } from './utils/routing/set-title';
import { formatCancerTypeNameWithCapitalization } from './utils/cancer/types/format-cancer-type-name-with-capitalization';

function lazyRoute(
  path: string,
  importFn: () => Promise<{ default: ComponentType }>,
  title?: string,
): RouteObject {
  return {
    path,
    ...(title ? { loader: setTitleFromLoader(title) } : {}),
    lazy: async () => ({ Component: (await importFn()).default }),
  };
}

function lazyRouteNamed(
  path: string,
  importFn: () => Promise<Record<string, ComponentType>>,
  exportName: string,
  title?: string,
): RouteObject {
  return {
    path,
    ...(title ? { loader: setTitleFromLoader(title) } : {}),
    lazy: async () => ({ Component: (await importFn())[exportName] }),
  };
}

// Redirect removed routes to sign-up
const deadRouteRedirect = (path: string): RouteObject => ({
  path,
  loader: () => redirect(ROUTE_SIGN_UP),
});

// Custom loaders for routes with dynamic titles
const singleCancerViewLoader: LoaderFunction = ({ params }) => {
  document.title = `${formatCancerTypeNameWithCapitalization(params.cancerId ?? '')} Cancer`;
  return null;
};

const testDetailsLoader: LoaderFunction = ({ params }) => {
  setTitle(
    `Testing Recommendation - ${(params.idTest ?? '').replace('_', ' ')}`,
  );
  return null;
};

const completeTestLoader: LoaderFunction = ({ params }) => {
  document.title = `${formatCancerTypeNameWithCapitalization(params.idTestOption ?? '')}`;
  return null;
};

const questionnaireV2QuestionLoader: LoaderFunction = ({ params }) => {
  document.title = `Risk Assessment - Question ${params.questionSlug}`;
  return null;
};

const router: Array<RouteObject> = [
  {
    loader({ request }) {
      if (authService.isLoggedIn() || hasWorkosSession()) {
        return null;
      }

      // Don't redirect while AuthKit is processing an OAuth callback.
      // OAuth callbacks include both `code` and `state` query parameters.
      const url = new URL(request.url);
      if (url.searchParams.has('code') && url.searchParams.has('state')) {
        return null;
      }

      return redirect(ROUTE_SIGN_IN);
    },
    children: [
      {
        Component: MainLayout,
        errorElement: <ChunkErrorBoundary />,
        children: [
          lazyRoute(ROUTE_ACCOUNT, () => import('./app/components/account/account.page'), 'Account'),
          lazyRoute(ROUTE_ACTION_PLAN, () => import('./app/components/action-plan/action-plan.page'), 'Action Plan'),
          lazyRoute(ROUTE_QUESTIONNAIRE, () => import('./app/components/questionnaire/questionnaire.page'), 'Risk Assessment'),
          lazyRoute(ROUTE_TESTING_PLAN, () => import('./app/components/testing-plan/testing-plan.page'), 'Testing Plan'),
          {
            path: ROUTE_TEST_DETAILS,
            loader: testDetailsLoader,
            lazy: async () => ({
              Component: (await import('./app/components/testing-plan/components/test-details/test-details.page')).default,
            }),
          },
          {
            path: ROUTE_TESTING_PLAN_COMPLETE,
            loader: completeTestLoader,
            lazy: async () => ({
              Component: (await import('./app/components/testing-plan/components/your-testing-plan/components/complete-test/complete-test.page')).default,
            }),
          },
          lazyRoute(ROUTE_DASHBOARD, () => import('./app/components/dashboard/dashboard.page'), 'Dashboard'),
          lazyRoute(ROUTE_BUILD_ACTION_PLAN, () => import('./app/components/action-plan/components/build-action-plan/build-action-plan.page'), 'Action Plan Builder'),
          lazyRoute(ROUTE_ACCOUNT_EDIT_MEMBERSHIP, () => import('./app/components/account/components/edit-membership/edit-membership.page'), 'Membership'),
          lazyRoute(ROUTE_RISK_ANALYSIS, () => import('./app/components/insights/insights.page'), 'Insights'),
          lazyRoute(ROUTE_RISK_TREND, () => import('./app/components/risks/risk-trend/risk-trend.page'), 'Risk Trend'),
          {
            path: ROUTE_SINGLE_CANCER_VIEW,
            loader: singleCancerViewLoader,
            lazy: async () => ({
              Component: (await import('./app/components/risks/single-cancer-view/single-cancer-view.page')).default,
            }),
          },
          lazyRoute(ROUTE_ACCOUNT_EDIT_COMMUNICATION_PREFERENCES, () => import('./app/components/account/components/edit-comm-preferences/edit-comm-preferences.page'), 'Communication Preferences'),
          lazyRoute(ROUTE_LOADING_SCREEN, () => import('./app/components/loading-screen/loading-screen.page'), 'Loading Screen'),
          lazyRoute(ROUTE_MULTI_DISEASE_WELCOME, () => import('./app/components/multi-disease-welcome/multi-disease-welcome.page')),
          {
            path: ROUTE_QUESTIONNAIRE_V2,
            children: [
              {
                index: true,
                loader: setTitleFromLoader('Risk Assessment'),
                lazy: async () => ({
                  Component: (await import('./app/components/questionnaireV2/components/questionnaireV2.route')).WrappedQuestionnaireV2,
                }),
              },
              {
                path: ':questionSlug',
                loader: questionnaireV2QuestionLoader,
                lazy: async () => ({
                  Component: (await import('./app/components/questionnaireV2/components/questionnaireV2.route')).WrappedQuestionnaireV2,
                }),
              },
            ],
          },
          lazyRoute(ROUTE_RISK_SCORE_REVEAL, () => import('./app/components/risk-score-reveal/risk-score-reveal.page'), 'Risk Score Reveal'),
          lazyRoute(ROUTE_BLOODWORK_REVEAL, () => import('./app/components/bloodwork-reveal/bloodwork-reveal.page'), 'Bloodwork Reveal'),
          lazyRouteNamed(ROUTE_LABS, () => import('./app/components/labs/labs.page'), 'Page', 'Catch Labs'),
          lazyRouteNamed(ROUTE_LABS_ALL, () => import('./app/components/labs/labs-all.page'), 'Page', 'All Biomarkers'),
          lazyRouteNamed(ROUTE_BIOMARKER_DETAIL, () => import('./app/components/labs/components/biomarker-detail/biomarker-detail.page'), 'Page', 'Biomarker Details'),
          {
            path: ROUTE_LABS_ORDER,
            loader: setTitleFromLoader('Labs Order'),
            lazy: async () => ({
              Component: (await import('./app/components/labs-order/labs-order.page')).Page,
            }),
            children: [
              {
                path: ROUTE_LABS_ORDER_STEP_1,
                lazy: async () => ({
                  Component: (await import('./app/components/labs-order/steps/step-1-summary')).Step1Summary,
                }),
              },
              {
                path: ROUTE_LABS_ORDER_STEP_2,
                lazy: async () => ({
                  Component: (await import('./app/components/labs-order/steps/step-2-user-info')).Step2UserInfo,
                }),
              },
              {
                path: ROUTE_LABS_ORDER_STEP_3,
                lazy: async () => ({
                  Component: (await import('./app/components/labs-order/steps/step-3-select-lab')).Step3SelectLab,
                }),
              },
              {
                path: ROUTE_LABS_ORDER_STEP_5,
                lazy: async () => ({
                  Component: (await import('./app/components/labs-order/steps/step-5-success')).Step5Success,
                }),
              },
            ],
          },
          lazyRouteNamed(ROUTE_CONSENT, () => import('./app/components/consent/consent.page'), 'Page', 'Consent Document'),
          lazyRoute(ROUTE_HEALTH_DATA, () => import('./app/components/health-data/health-data.page'), 'Health Data'),
        ],
      },
    ],
  },
  {
    Component: Layout,
    children: [
      routeAuthMagic,
      routeSignUp,
      routeSignIn,
      routePayment,
      routeJoinFunnel,
      // Active quizlet routes (v6 flow)
      routeQuizletVariant,
      ...generateAllQuizletRoutes(getEnabledQuizletVersions()),

      // Dead routes — catch-all redirects to sign-up
      deadRouteRedirect('/free-assessment/*'),
      deadRouteRedirect('/free-assessment'),
      deadRouteRedirect('/early-access/*'),
      deadRouteRedirect('/early-access'),
      deadRouteRedirect('/quiz/*'),
      deadRouteRedirect('/quiz'),
    ],
  },
];

export default router;
